import { useState, useMemo, useCallback } from "react";
import { Grid } from "@mui/material";
import dayjs from "dayjs";
import { DateInput } from "../../../../lib/inputs";
import { RegistrationFieldLabels } from "../../../../config/RegistrationConfigs";
import SexInput from "./SexInput";
import NationalityInput from "./NationalityInput";
import BirthNumberInput, { normalizeBirthNumber, BIRTH_NUMBER_MASTERS_HELPER_TEXT } from "./BirthNumberInput";
import { useForm, useFormValidation } from "../../../../lib/common";
import { validateBirthNumber } from "./BirthDataUtils";
import { isIdentityFieldDisabled } from "../../Shared/WizardModes";
import DateUtils from "../../../../utils/DateUtils";
import { RaceSeries } from "../../../../config/RaceConfigs";

// Nationality ISO codes for CZ/SK countries
const KNOWN_NATIONALITY_FORMATS = ["CZ", "SK"];
const DEFAULT_NATIONALITY = "CZ";

/**
 * BirthDataInput - Component for nationality, birth number, and birthday fields
 * Handles conditional validation and auto-calculation of birthday from birth number
 *
 * @param {Object} props
 * @param {Object} [props.registration] - Registration used for prefilled values; omit on standalone forms (e.g. sign-up).
 * @param {Object} [props.race] - Race used for minimum age / max birth date; omit to disable those constraints.
 * @param {string} props.mode - Mode of the step ("new" | "detail" | "edit")
 * @param {Object} [props.user] - App user for per-field identity locks
 */
export default function BirthDataInput({ registration, race, mode, user }) {
  const form = useForm();

  // Initialize form values during initial state creation (lazy initialization)
  // This ensures values are set once on mount and not repopulated on registration updates
  const initialValues = useMemo(() => {
    const noRegistration = registration == null;
    const initialNationality = noRegistration ? "" : registration?.nationality || DEFAULT_NATIONALITY;
    const initialSex = noRegistration ? "" : registration?.sex || "";
    const initialBirthNumber = noRegistration ? "" : normalizeBirthNumber(registration?.birthNumber || "");
    const initialBirthday = noRegistration
      ? null
      : registration?.birthdate
        ? dayjs.isDayjs(registration.birthdate)
          ? registration.birthdate
          : DateUtils.toPragueDateOnly(registration.birthdate)
        : null;

    // Set form values during initialization
    if (registration?._id) {
      form.setValue("_id", registration._id);
    }
    if (initialNationality) {
      form.setValue("nationality", initialNationality);
    }
    if (initialSex) {
      form.setValue("sex", initialSex);
    }
    if (initialBirthNumber) {
      form.setValue("birthNumber", initialBirthNumber);
    }
    if (initialBirthday && initialBirthday.isValid()) {
      form.setValue("birthdate", initialBirthday);
    }

    return {
      nationality: initialNationality,
      sex: initialSex,
      birthNumber: initialBirthNumber,
      birthday: initialBirthday && initialBirthday.isValid() ? initialBirthday : null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only initialize on registration ID change to prevent repopulation
  }, [registration?._id]); // Only re-run if registration ID changes (new registration)

  const [nationality, setNationality] = useState(initialValues.nationality);
  const [sex, setSex] = useState(initialValues.sex);
  const [birthNumber, setBirthNumber] = useState(initialValues.birthNumber);
  const [birthNumberError, setBirthNumberError] = useState(null);
  const [birthday, setBirthday] = useState(initialValues.birthday);

  // Determine if birthday should be disabled
  const isBirthdayDisabled = !nationality || KNOWN_NATIONALITY_FORMATS.includes(nationality);

  // Calculate maxDate for birthdate based on minimum age requirement
  const maxBirthdate = useMemo(() => {
    if (!race?.minimumAge || !race?.raceDateInterval?.from) {
      return null;
    }
    const raceStartDate = dayjs(race.raceDateInterval.from);
    return raceStartDate.subtract(race.minimumAge, "year");
  }, [race?.minimumAge, race?.raceDateInterval?.from]);

  // Helper function to validate minimum age for a given birthdate
  const validateMinimumAge = useCallback(
    (birthdateValue) => {
      if (!birthdateValue || !race?.minimumAge || !race?.raceDateInterval?.from) {
        return null;
      }
      const birthdateDayjs = dayjs.isDayjs(birthdateValue) ? birthdateValue : dayjs(birthdateValue);
      if (!birthdateDayjs.isValid()) {
        return null; // Invalid date will be caught by DateInput validation
      }
      const raceStartDate = dayjs(race.raceDateInterval.from);
      const ageAtRaceStart = raceStartDate.diff(birthdateDayjs, "year");
      if (ageAtRaceStart < race.minimumAge) {
        return `Minimální věk pro tuto akci je ${race.minimumAge} let. Váš věk při startu bude ${ageAtRaceStart} let.`;
      }
      return null;
    },
    [race?.minimumAge, race?.raceDateInterval?.from],
  );

  // Validate minimum age for current birthday state
  const birthdayError = useMemo(() => validateMinimumAge(birthday), [birthday, validateMinimumAge]);

  // Handle nationality change
  function handleNationalityChange(newNationality) {
    setNationality(newNationality || "");
    form.setValue("nationality", newNationality || "");

    // If switching to CZ/SK and birth number exists, validate and recalculate birthday
    if (KNOWN_NATIONALITY_FORMATS.includes(newNationality) && birthNumber) {
      validateAndCalculateBirthday(birthNumber, newNationality, sex);
    } else if (newNationality && !KNOWN_NATIONALITY_FORMATS.includes(newNationality)) {
      // Non-CZ/SK nationality - clear birthday if it was auto-calculated
      // Keep existing birthday if user manually entered it
    }
  }

  // Handle sex change
  function handleSexChange(newSex) {
    setSex(newSex);
    form.setValue("sex", newSex);

    // If CZ/SK nationality and birth number exists, revalidate with new sex
    if (KNOWN_NATIONALITY_FORMATS.includes(nationality) && birthNumber) {
      validateAndCalculateBirthday(birthNumber, nationality, newSex);
    }
  }

  // Validate birth number and calculate birthday
  function validateAndCalculateBirthday(value, currentNationality, currentSex) {
    const currentNat = currentNationality || nationality;
    const currentSexValue = currentSex !== undefined ? currentSex : sex;

    // Normalize the value (remove spaces and slashes)
    const normalizedValue = normalizeBirthNumber(value);

    // Always set the normalized value in the form (without slash)
    form.setValue("birthNumber", normalizedValue);

    // Update state with normalized value for display consistency
    setBirthNumber(normalizedValue);

    if (!normalizedValue) {
      setBirthNumberError(null);
      return;
    }

    // Validate format only for CZ/SK
    if (KNOWN_NATIONALITY_FORMATS.includes(currentNat)) {
      const validation = validateBirthNumber(normalizedValue, currentSexValue);

      if (!validation.valid) {
        setBirthNumberError(validation.error);
        return;
      }

      // Set calculated birthday from validation result
      if (validation.birthday) {
        setBirthday(validation.birthday);
        form.setValue("birthdate", validation.birthday);
      }
    }

    // For non-CZ/SK nationalities, no format validation, just accept the value
    setBirthNumberError(null);
  }

  // Determine if birthNumber is required (only for CZ/SK nationalities)
  const isBirthNumberRequired = KNOWN_NATIONALITY_FORMATS.includes(nationality);

  // Register form validation for birth number
  useFormValidation(
    "birthNumber",
    (formValues) => {
      const formNationality = formValues.nationality || nationality;
      const formBirthNumber = normalizeBirthNumber(formValues.birthNumber || birthNumber || "");
      const formSex = formValues.sex || sex;

      // Only validate for CZ/SK nationalities
      if (!KNOWN_NATIONALITY_FORMATS.includes(formNationality)) {
        return true; // Not required for non-CZ/SK
      }

      // If required but empty, validation fails
      if (!formBirthNumber) {
        return false;
      }

      // Validate the birth number format
      const validation = validateBirthNumber(formBirthNumber, formSex);
      return validation.valid;
    },
    [nationality, birthNumber, sex],
  );

  // Register form validation for birthdate (minimum age requirement)
  useFormValidation(
    "birthdate",
    (formValues) => {
      const formBirthdate = formValues.birthdate || birthday;

      // If no birthdate, let required validation handle it
      if (!formBirthdate) {
        return true;
      }

      // Reuse the existing validation logic
      const error = validateMinimumAge(formBirthdate);
      return error === null; // Valid if no error
    },
    [birthday, race?.minimumAge, race?.raceDateInterval?.from],
  );

  // Handle birth number change
  function handleBirthNumberChange(normalizedValue) {
    validateAndCalculateBirthday(normalizedValue, nationality, sex);
  }

  // Handle birthday change (when user manually types or selects date)
  function handleBirthdayChange(newBirthday) {
    setBirthday(newBirthday);
    form.setValue("birthdate", newBirthday);
  }

  return (
    <>
      <Grid size={{ xs: 12, md: 6 }}>
        <SexInput value={sex} onChange={handleSexChange} disabled={isIdentityFieldDisabled(mode, "sex", user)} />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <NationalityInput
          value={nationality}
          onChange={handleNationalityChange}
          disabled={isIdentityFieldDisabled(mode, "nationality", user)}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <BirthNumberInput
          value={birthNumber}
          nationality={nationality}
          error={birthNumberError}
          onChange={handleBirthNumberChange}
          required={isBirthNumberRequired}
          disabled={isIdentityFieldDisabled(mode, "birthNumber", user)}
          helperText={race?.series === RaceSeries.b7.key ? BIRTH_NUMBER_MASTERS_HELPER_TEXT : ""}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <DateInput
          name="birthdate"
          label={RegistrationFieldLabels.birthdate}
          required
          value={birthday}
          defaultValue={registration?.birthdate}
          onChange={handleBirthdayChange}
          openTo="year"
          views={["year", "month", "day"]}
          size="small"
          maxDate={maxBirthdate}
          error={birthdayError}
          disabled={isBirthdayDisabled || isIdentityFieldDisabled(mode, "birthdate", user)}
        />
      </Grid>
    </>
  );
}
